import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import { CATEGORY_ICONS } from '@/utils/format';
import Button from '@/components/ui/Button';
import { ArrowRight, Check } from 'lucide-react';

const STEPS = ['welcome', 'income', 'budget', 'goal'];

export default function Onboarding({ onComplete }) {
  const { t } = useTranslation();
  const addTransaction = useStore((s) => s.addTransaction);
  const addBudget = useStore((s) => s.addBudget);
  const addGoal = useStore((s) => s.addGoal);

  const [step, setStep] = useState(0);
  const [incomeAmount, setIncomeAmount] = useState('');
  const [budgetCategory, setBudgetCategory] = useState('food');
  const [budgetLimit, setBudgetLimit] = useState('');
  const [goalName, setGoalName] = useState('');
  const [goalTarget, setGoalTarget] = useState('');

  const handleNext = () => {
    if (step === 1 && incomeAmount) {
      addTransaction({
        description: t('categories.salary'),
        amount: Number(incomeAmount),
        type: 'income',
        category: 'salary',
        date: new Date().toISOString().split('T')[0],
      });
    }
    if (step === 2 && budgetLimit) {
      addBudget({ category: budgetCategory, limit: Number(budgetLimit), spent: 0 });
    }
    if (step === 3 && goalName && goalTarget) {
      addGoal({ name: goalName, target: Number(goalTarget), saved: 0, icon: '🎯' });
    }

    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      onComplete();
    }
  };

  const canSkip = step > 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= step ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-800'
              }`}
            />
          ))}
        </div>

        <div className="card space-y-6">
          {step === 0 && (
            <>
              <div className="text-center">
                <div className="text-5xl mb-4">👋</div>
                <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-white mb-2">
                  {t('onboarding.welcome.title')}
                </h1>
                <p className="text-sm text-gray-500">{t('onboarding.welcome.description')}</p>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="text-center">
                <div className="text-5xl mb-4">💰</div>
                <h2 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-2">
                  {t('onboarding.income.title')}
                </h2>
                <p className="text-sm text-gray-500">{t('onboarding.income.description')}</p>
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-300">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={incomeAmount}
                  onChange={(e) => setIncomeAmount(e.target.value)}
                  className="w-full pl-10 pr-4 py-4 text-2xl font-bold input"
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="text-center">
                <div className="text-5xl mb-4">📊</div>
                <h2 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-2">
                  {t('onboarding.budget.title')}
                </h2>
                <p className="text-sm text-gray-500">{t('onboarding.budget.description')}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['food', 'transport', 'entertainment'].map((c) => (
                  <button
                    key={c}
                    onClick={() => setBudgetCategory(c)}
                    className={`py-2 px-3 text-xs font-medium rounded-xl border transition-all flex flex-col items-center gap-1 ${
                      budgetCategory === c
                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                    }`}
                  >
                    <span>{CATEGORY_ICONS[c]}</span>
                    {t(`categories.${c}`)}
                  </button>
                ))}
              </div>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-300">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={budgetLimit}
                  onChange={(e) => setBudgetLimit(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 text-lg font-bold input"
                />
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="text-center">
                <div className="text-5xl mb-4">🎯</div>
                <h2 className="text-xl font-display font-bold text-gray-900 dark:text-white mb-2">
                  {t('onboarding.goal.title')}
                </h2>
                <p className="text-sm text-gray-500">{t('onboarding.goal.description')}</p>
              </div>
              <input
                type="text"
                className="input"
                placeholder={t('onboarding.goal.namePlaceholder')}
                value={goalName}
                onChange={(e) => setGoalName(e.target.value)}
              />
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-300">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={goalTarget}
                  onChange={(e) => setGoalTarget(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 text-lg font-bold input"
                />
              </div>
            </>
          )}

          <div className="flex gap-3">
            {canSkip && (
              <Button variant="ghost" className="flex-1" onClick={handleNext}>
                {t('onboarding.skip')}
              </Button>
            )}
            <Button className="flex-1" onClick={handleNext} icon={step === STEPS.length - 1 ? Check : ArrowRight}>
              {step === STEPS.length - 1 ? t('onboarding.finish') : t('onboarding.next')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
